"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "kitchen-space-planner",
  "refrigerator-lifespan-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Ice Maker Size Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Size a standalone ice maker by daily users and venue. We use restaurant-industry consumption rates (lbs per person per day) to recommend production capacity and storage bin size.",
  faqs: [
    {
      question: "How much ice does the average household use?",
      answer:
        "Typical home use is about 1.5 lbs of ice per person per day, jumping to 2 lbs during entertaining. Restaurants run closer to 3 lbs per customer per day, and full bars can hit 5 lbs per customer.",
    },
    {
      question: "Why should the storage bin only hold half a day's production?",
      answer:
        "Ice melts and refreezes inside the bin if it sits too long, fusing into a solid block. Sizing the bin to roughly 50% of daily production keeps ice rotating and prevents that.",
    },
    {
      question: "Cube, nugget, flake, or clear — which ice type should I pick?",
      answer:
        "Cubes melt slowest and suit drinks. Nugget (chewable) is popular for soft drinks and cocktails. Flake is moldable and ideal for seafood displays. Clear ice is premium and slow-melting for high-end cocktails.",
    },
    {
      question: "Do I need plumbing for these units?",
      answer:
        "Anything producing more than ~30 lbs/day typically needs a dedicated water line and drain. Smaller portable countertop units run from a reservoir you fill manually.",
    },
    {
      question: "What does NSF certification mean?",
      answer:
        "NSF certification confirms the unit meets sanitation standards for commercial food-service environments. Restaurants and bars usually require it; home users don't.",
    },
  ],
  relatedTools: ["refrigerator-size-calculator", "energy-cost-calculator"],
}

export function StandaloneIceMakerSizePageClient() {
  const [dailyUsers, setDailyUsers] = useState("8")
  const [usage, setUsage] = useState("home")
  const [iceType, setIceType] = useState("cube")

  const result = useMemo(() => {
    const users = parseInt(dailyUsers)
    if (!users || users <= 0) return null

    // Original consumption rates preserved verbatim.
    let lbsPerPerson = 0
    if (usage === "home") lbsPerPerson = 1.5
    else if (usage === "office") lbsPerPerson = 1
    else if (usage === "restaurant") lbsPerPerson = 3
    else if (usage === "bar") lbsPerPerson = 5
    else lbsPerPerson = 2 // party/event

    const dailyProduction = users * lbsPerPerson
    const storageBin = Math.ceil(dailyProduction * 0.5)

    let recommendedSize: string
    if (dailyProduction < 25) recommendedSize = "Portable ice maker (20–30 lbs/day)"
    else if (dailyProduction < 50) recommendedSize = "Undercounter ice maker (40–60 lbs/day)"
    else if (dailyProduction < 100) recommendedSize = "Commercial undercounter (80–120 lbs/day)"
    else if (dailyProduction < 200) recommendedSize = "Modular ice machine (150–250 lbs/day)"
    else recommendedSize = "Commercial ice machine (250+ lbs/day)"

    return { dailyProduction, storageBin, recommendedSize, iceType }
  }, [dailyUsers, usage, iceType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="How many pounds of ice"
      heroTitleItalic="per day"
      heroTitlePost="do you actually need?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Industry consumption rates"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "1.5", label: "Lbs / home person", note: "Per day" },
            { value: "3", label: "Lbs / restaurant", note: "Per customer" },
            { value: "5", label: "Lbs / bar", note: "Per customer" },
            { value: "50%", label: "Bin sizing rule", note: "Of daily" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "ice-maker-size-calculator")}
      cta={{
        title: "Stand-alone ice production, plus everything else cold.",
        body: "Browse undercounter and countertop ice makers sized to fit kitchens, bars, and outdoor spaces.",
        buttonLabel: "Browse ice makers",
        buttonHref: "/ice-makers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Tell us where
                <br />
                the ice goes.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Consumption per person varies by ~3× from a quiet home to a busy bar.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Daily users
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={dailyUsers}
                      onChange={(e) => setDailyUsers(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">people</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Usage type
                  </label>
                  <select
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="home">Home (1.5 lbs/person)</option>
                    <option value="office">Office (1 lb/person)</option>
                    <option value="restaurant">Restaurant (3 lbs/customer)</option>
                    <option value="bar">Bar (5 lbs/customer)</option>
                    <option value="party">Party / event (2 lbs/person)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Preferred ice type
                  </label>
                  <select
                    value={iceType}
                    onChange={(e) => setIceType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="cube">Cube — slow-melting, drinks</option>
                    <option value="nugget">Nugget — chewable</option>
                    <option value="flake">Flake — moldable, displays</option>
                    <option value="clear">Clear — premium cocktails</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Cube is the safest default.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Daily ice production
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.dailyProduction ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">lbs/day</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendedSize ?? "Enter daily users to see capacity."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Storage bin</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.storageBin ?? "—"} lbs
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Ice type</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight capitalize">
                        {iceType}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Or pick a fridge with ice"
          title="Bottom-freezer fridges with built-in ice makers"
          description="Skip the standalone unit — modern French door and bottom-freezer fridges produce 5–10 lbs/day."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Industry rates. <em className="italic text-[#ff9951]">Real</em> bins.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Per-person consumption rates come from foodservice industry sizing guidance used by Hoshizaki, Manitowoc, and Scotsman.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Daily production",
                  d: "Users × per-person rate (1 lb office → 5 lbs bar). Industry-standard sizing.",
                },
                {
                  n: "02",
                  t: "Bin sizing rule",
                  d: "Storage = 50% of daily production. Smaller bins force ice to rotate so it doesn't fuse into a block.",
                },
                {
                  n: "03",
                  t: "Plumbing threshold",
                  d: "Anything producing 30+ lbs/day needs a water line and drain. Below that, portable models work.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges — best ice configurations"
          description="Models with dual ice makers and exterior dispensers for high-volume households."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
